import { useMutation, useQuery } from '@apollo/client/react';
import { CombinedGraphQLErrors } from '@apollo/client';
import { GraphQLErrorCode } from '@insio/validation';
import { useRef, useState } from 'react';
import { Button, Card, Paper } from '@insio/ui-toolkit';
import { useTranslation } from 'react-i18next';
import { BillingModel, ExtractionQuotaStatus } from '@insio/shared-types';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { isMasterContext, useAuth } from '@/features/auth';
import {
  CreditUsageCallToAction,
  CreditUsageSummary,
  CreditViewer,
  PendingPlanUpgradeNotice,
  creditViewerOf,
  useCanManageBilling,
  useCreditUsage,
  usePendingPlanUpgrade
} from '@/features/credits';
import { useToastHelpers } from '@/hooks/use-toast-helpers';
import {
  CancelScheduledPlanChangeDocument,
  ChangeSubscriptionPlanDocument,
  CreateBillingPortalSessionDocument,
  GetCurrentBillingDocument,
  GetProfileDocument,
  GetPublicPricingPlansDocument,
  PendingPlanUpgradeDocument,
  ResumeSubscriptionDocument,
  ScheduleSubscriptionCancellationDocument
} from '@/graphql';
import { BillingHeroPanel } from './BillingHeroPanel';
import { CancelSubscriptionModal } from './CancelSubscriptionModal';
import { ChangePlanModal, type PlanChangeDirection } from './ChangePlanModal';
import { PlanCard, type PlanCardAction } from './PlanCard';
import { useStripeCheckout } from '../../hooks/use-stripe-checkout';
import { toQuotaInfo } from '../../utils/quota-utils';

/** Whether the backend reported the billing portal as temporarily unavailable (503). */
function isBillingPortalUnavailable(error: unknown): boolean {
  return (
    CombinedGraphQLErrors.is(error) &&
    error.errors.some(
      e =>
        e.extensions?.['code'] === GraphQLErrorCode.SERVICE_UNAVAILABLE &&
        e.extensions?.['errorCode'] === GraphQLErrorCode.BILLING_PORTAL_UNAVAILABLE
    )
  );
}

interface ChangeModalTarget {
  priceId: string;
  planName: string;
  direction: PlanChangeDirection;
  /** An upgrade within credit plans, which Stripe applies once its invoice is paid. */
  paidFirst: boolean;
}

export function PaymentsSettings() {
  const t = useFeatureTranslation('systemSettings.payments');
  const tCredits = useFeatureTranslation('credits.billing');
  const { i18n } = useTranslation();
  const { user } = useAuth();
  const { showError } = useToastHelpers();
  const onMasterAccountBoard = isMasterContext(user);
  const canManageBilling = useCanManageBilling();

  // A credit payer's table shows its credits in place of the document quota row.
  const { creditUsage, loading: creditUsageLoading } = useCreditUsage();
  const isCreditPayer = creditViewerOf(creditUsage) === CreditViewer.PAYER;
  const showCreditBilling = isCreditPayer && canManageBilling;
  const pendingUpgrade = usePendingPlanUpgrade({ skip: !showCreditBilling });
  const plansRef = useRef<HTMLDivElement>(null);

  const { data } = useQuery(GetProfileDocument, { fetchPolicy: 'cache-and-network' });
  // A managed client is billed through its master account — it has no subscription or plans of its own.
  const managedByMasterAccountName = data?.profile?.account?.managingMasterAccount?.name ?? null;
  const isManagedClient = !!managedByMasterAccountName;
  // The master account's own books draw from the pool their own people manage — nobody to contact.
  const isMasterAccountOwnBooks = data?.profile?.account?.isMasterAccountOwnBooks ?? false;

  const {
    data: subscriptionData,
    error: subscriptionError,
    refetch: refetchSubscription
  } = useQuery(GetCurrentBillingDocument, {
    fetchPolicy: 'cache-and-network',
    skip: isManagedClient
  });
  // Answered, even with no subscription or with an error: the credits do not depend on it.
  const subscriptionAnswered = subscriptionData !== undefined || subscriptionError !== undefined;
  const { data: publicPlansData } = useQuery(GetPublicPricingPlansDocument, {
    fetchPolicy: 'cache-and-network',
    skip: isManagedClient
  });

  const { initiateCheckout, loadingPriceId, error: stripeError } = useStripeCheckout();

  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [changeModalTarget, setChangeModalTarget] = useState<ChangeModalTarget | null>(null);

  const [scheduleCancellation, { loading: scheduling }] = useMutation(
    ScheduleSubscriptionCancellationDocument,
    {
      onCompleted: () => {
        setCancelModalOpen(false);
        void refetchSubscription();
      }
    }
  );
  const [resumeSubscription, { loading: resuming }] = useMutation(ResumeSubscriptionDocument, {
    onCompleted: () => {
      void refetchSubscription();
    }
  });
  const [changeSubscriptionPlan, { loading: changing }] = useMutation(
    ChangeSubscriptionPlanDocument,
    {
      // A credit upgrade waits for its invoice; the notice above the table shows it as pending.
      refetchQueries: showCreditBilling ? [PendingPlanUpgradeDocument] : [],
      onCompleted: () => {
        setChangeModalTarget(null);
        void refetchSubscription();
      }
    }
  );
  const [cancelScheduledPlanChange, { loading: cancellingChange }] = useMutation(
    CancelScheduledPlanChangeDocument,
    {
      onCompleted: () => {
        void refetchSubscription();
      }
    }
  );

  const [createPortalSession, { loading: openingPortal }] = useMutation(
    CreateBillingPortalSessionDocument
  );

  const handleOpenPortal = async () => {
    try {
      const result = await createPortalSession({ variables: { input: {} } });
      // errorPolicy 'all' resolves a GraphQL refusal instead of throwing it.
      if (result.error) throw result.error;
      const url = result.data?.createBillingPortalSession?.url;
      if (!url) {
        showError(t('portal.error'));
        return;
      }
      window.location.href = url;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to open billing portal', error);
      showError(isBillingPortalUnavailable(error) ? t('portal.unavailable') : t('portal.error'));
    }
  };

  const rawQuota = data?.profile?.account?.extractionQuota;
  // Figures exist only for an account billed on its own; a managed client gets a status.
  const extractionQuota = toQuotaInfo(rawQuota);
  const quotaExhausted = rawQuota?.status === ExtractionQuotaStatus.EXHAUSTED;

  const subscription = subscriptionData?.currentSubscription;
  const currentPrice = subscription?.price ?? null;
  const currentPlanId = currentPrice?.plan?.id ?? null;
  const currentAmount = currentPrice?.amount ?? null;
  const heroPlan = currentPrice?.plan
    ? {
        name: currentPrice.plan.name,
        billingProvider: currentPrice.plan.billingProvider,
        billingModel: currentPrice.plan.billingModel,
        maxDocumentsPerMonth: currentPrice.plan.maxDocumentsPerMonth,
        monthlyCredits: currentPrice.plan.monthlyCredits ?? null,
        canExceedLimit: extractionQuota?.canExceedLimit ?? false,
        amount: currentPrice.amount != null ? String(currentPrice.amount) : '0',
        currency: currentPrice.currency,
        pricePerExtraDocument:
          currentPrice.pricePerExtraDocument != null
            ? String(currentPrice.pricePerExtraDocument)
            : null
      }
    : null;

  const currentBillingProvider = currentPrice?.plan?.billingProvider ?? null;
  const isManualAccount = currentBillingProvider === 'MANUAL';
  const cancelAtPeriodEnd = subscription?.cancelAtPeriodEnd ?? false;
  const scheduledPrice = subscription?.scheduledPrice ?? null;
  const planChangeScheduled = !!scheduledPrice;
  const currentPeriodEnd = subscription?.currentPeriodEnd ?? null;
  const scheduledChangeAt = subscription?.scheduledChangeAt ?? null;
  const formatDate = (instant: typeof currentPeriodEnd) =>
    instant
      ? instant
          .toZonedDateTimeISO('UTC')
          .toPlainDate()
          .toLocaleString(i18n.language, { day: 'numeric', month: 'long', year: 'numeric' })
      : null;
  const periodEndLabel = formatDate(currentPeriodEnd);
  const scheduledChangeLabel = formatDate(scheduledChangeAt) ?? periodEndLabel;

  const hasStripeSubscription = currentBillingProvider === 'STRIPE';
  const mutationLoading = scheduling || resuming || changing || cancellingChange;

  const monthlyAmount = (planPrices: { interval: string; amount: { toNumber: () => number } }[]) =>
    planPrices.find(p => p.interval === 'MONTH')?.amount.toNumber() ?? Number.POSITIVE_INFINITY;

  const plans = [...(publicPlansData?.publicPricingPlans ?? [])].sort((a, b) => {
    const aFree = a.billingProvider === 'FREE' ? 0 : 1;
    const bFree = b.billingProvider === 'FREE' ? 0 : 1;
    if (aFree !== bFree) return aFree - bFree;
    return monthlyAmount(a.activePrices ?? []) - monthlyAmount(b.activePrices ?? []);
  });

  const showPlans = !isManualAccount && plans.length > 0;
  const scrollToPlans = () =>
    plansRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  const freePlan = plans.find(p => p.billingProvider === 'FREE') ?? null;
  const freeLimit = freePlan?.maxDocumentsPerMonth ?? 0;
  const freeBillingModel = freePlan?.billingModel ?? BillingModel.DOCUMENTS;
  const freeCredits = freePlan?.monthlyCredits ?? null;

  const computeAction = (plan: (typeof plans)[number]): PlanCardAction => {
    const monthlyPrice = (plan.activePrices ?? []).find(p => p.interval === 'MONTH');
    const isCurrent = plan.id === currentPlanId;

    // Members see the catalogue read-only; every mutating action is BILLING_MANAGE-gated.
    if (!canManageBilling) {
      return isCurrent ? { type: 'currentPlan' } : { type: 'disabled' };
    }

    if (isCurrent) {
      if (planChangeScheduled || cancelAtPeriodEnd) {
        return {
          type: 'cancelScheduledChange',
          loading: cancellingChange || resuming,
          onClick: () => {
            if (planChangeScheduled) {
              void cancelScheduledPlanChange();
            } else {
              void resumeSubscription();
            }
          }
        };
      }
      return { type: 'currentPlan' };
    }

    if (plan.billingProvider === 'FREE') {
      if (hasStripeSubscription && !cancelAtPeriodEnd && !planChangeScheduled) {
        return {
          type: 'cancelSubscription',
          loading: scheduling,
          onClick: () => setCancelModalOpen(true)
        };
      }
      return { type: 'disabled' };
    }

    if (plan.billingProvider !== 'STRIPE' || !monthlyPrice) {
      return { type: 'disabled' };
    }

    if (!hasStripeSubscription) {
      return {
        type: 'checkout',
        loading: loadingPriceId === monthlyPrice.id,
        onClick: () => {
          void initiateCheckout(monthlyPrice.id);
        }
      };
    }

    const isUpgrade = currentAmount != null && monthlyPrice.amount.greaterThan(currentAmount);
    // Mirrors the backend: an upgrade that stays on credits is invoiced and applied once paid.
    const paidFirst =
      isUpgrade &&
      currentPrice?.plan?.billingModel === BillingModel.CREDITS &&
      plan.billingModel === BillingModel.CREDITS;
    return {
      type: isUpgrade ? 'upgrade' : 'scheduleDowngrade',
      loading: changing && changeModalTarget?.priceId === monthlyPrice.id,
      onClick: () =>
        setChangeModalTarget({
          priceId: monthlyPrice.id,
          planName: plan.name,
          direction: isUpgrade ? 'upgrade' : 'downgrade',
          paidFirst
        })
    };
  };

  // The plan, its billing and the cancel or scheduled-change actions, alike for both billing models.
  const subscriptionHeroProps = {
    plan: heroPlan,
    showCancelCta: hasStripeSubscription && !cancelAtPeriodEnd && !planChangeScheduled,
    canManage: canManageBilling,
    cancellationScheduled: hasStripeSubscription && cancelAtPeriodEnd,
    planChangeScheduled,
    scheduledPlanName: scheduledPrice?.plan?.name ?? null,
    periodEndLabel: planChangeScheduled ? scheduledChangeLabel : periodEndLabel,
    mutationLoading,
    onCancelClick: () => setCancelModalOpen(true),
    onResumeClick: () => {
      void resumeSubscription();
    },
    onCancelScheduledChange: () => {
      void cancelScheduledPlanChange();
    }
  };

  if (isManagedClient) {
    return (
      <div className='space-y-md'>
        <p className='text-sm text-foreground-muted'>{t('pageDescription')}</p>
        <Paper showDivider={false} className='px-0 sm:px-lg'>
          <Paper.Content>
            <div className='space-y-2xs'>
              <h2 className='text-base font-semibold text-foreground-base'>
                {t('managedByMasterAccount.title')}
              </h2>
              <p className='text-sm text-foreground-muted'>
                {t('managedByMasterAccount.description', {
                  masterAccountName: managedByMasterAccountName
                })}
              </p>
              {rawQuota && (
                <p
                  className={
                    quotaExhausted ? 'text-sm text-error-text' : 'text-sm text-foreground-muted'
                  }
                >
                  {quotaExhausted
                    ? isMasterAccountOwnBooks
                      ? t('managedByMasterAccount.ownBooksQuotaExhausted', {
                          masterAccountName: managedByMasterAccountName
                        })
                      : t('managedByMasterAccount.quotaExhausted', {
                          masterAccountName: managedByMasterAccountName
                        })
                    : t('managedByMasterAccount.quotaAvailable')}
                </p>
              )}
            </div>
          </Paper.Content>
        </Paper>
      </div>
    );
  }

  return (
    <div className='space-y-md'>
      <p className='text-sm text-foreground-muted'>
        {showCreditBilling ? t('pageDescriptionCredits') : t('pageDescription')}
      </p>

      {showCreditBilling && creditUsage && subscriptionAnswered && (
        <Paper showDivider={false} className='px-0 sm:px-lg'>
          <Paper.Content>
            <div className='flex flex-col gap-sm'>
              <PendingPlanUpgradeNotice upgrade={pendingUpgrade} />
              <BillingHeroPanel
                {...subscriptionHeroProps}
                quota={null}
                usage={{
                  label: tCredits('title'),
                  content: <CreditUsageSummary usage={creditUsage} />,
                  footer: (
                    <CreditUsageCallToAction
                      usage={creditUsage}
                      onUpgrade={showPlans ? scrollToPlans : null}
                      onOpenBillingPortal={
                        hasStripeSubscription ? () => void handleOpenPortal() : null
                      }
                      billingPortalOpening={openingPortal}
                    />
                  )
                }}
              />
            </div>
          </Paper.Content>
        </Paper>
      )}

      {/* The board has the master's subscription but no pool figures to show with it. */}
      {!isCreditPayer &&
        !creditUsageLoading &&
        (extractionQuota || (onMasterAccountBoard && heroPlan)) && (
          <Paper showDivider={false} className='px-0 sm:px-lg'>
            <Paper.Content>
              <BillingHeroPanel {...subscriptionHeroProps} quota={extractionQuota} />
            </Paper.Content>
          </Paper>
        )}

      {canManageBilling && hasStripeSubscription && (
        <Paper showDivider={false} className='py-xs px-0 sm:px-lg'>
          <Paper.Content>
            <Card className='gap-0 p-0'>
              <div className='flex flex-col gap-md px-md py-sm sm:flex-row sm:items-center sm:justify-between'>
                <div>
                  <h2 className='text-base font-semibold text-foreground-base'>
                    {t('portal.title')}
                  </h2>
                  <p className='text-sm text-foreground-muted mt-2xs'>{t('portal.description')}</p>
                </div>
                <Button
                  variant='primary'
                  icon='credit-card'
                  disabled={openingPortal}
                  onClick={() => void handleOpenPortal()}
                >
                  {openingPortal ? t('portal.opening') : t('portal.openButton')}
                </Button>
              </div>
            </Card>
          </Paper.Content>
        </Paper>
      )}

      {showPlans && (
        <Paper showDivider={false} className='px-0 sm:px-lg'>
          <Paper.Content>
            <div ref={plansRef} className='flex flex-col gap-md scroll-mt-lg'>
              <div>
                <h2 className='text-base font-semibold text-foreground-base'>
                  {t('allPlans.title')}
                </h2>
                <p className='text-sm text-foreground-muted mt-2xs'>{t('allPlans.description')}</p>
              </div>

              <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-md items-stretch'>
                {plans.map(plan => (
                  <PlanCard
                    key={plan.id}
                    planId={plan.id}
                    name={plan.name}
                    description={plan.localizedDescription}
                    billingModel={plan.billingModel}
                    maxDocumentsPerMonth={plan.maxDocumentsPerMonth}
                    monthlyCredits={plan.monthlyCredits}
                    canExceedLimit={plan.canExceedLimit}
                    billingProvider={plan.billingProvider}
                    prices={plan.activePrices ?? []}
                    isCurrentPlan={plan.id === currentPlanId}
                    action={computeAction(plan)}
                  />
                ))}
              </div>

              {stripeError && (
                <div className='rounded-md border border-error-border bg-error-bg px-md py-sm'>
                  <p className='text-sm text-error-text'>{stripeError}</p>
                </div>
              )}
            </div>
          </Paper.Content>
        </Paper>
      )}

      <CancelSubscriptionModal
        open={cancelModalOpen}
        loading={scheduling}
        periodEndLabel={periodEndLabel}
        freeBillingModel={freeBillingModel}
        freeLimit={freeLimit}
        freeCredits={freeCredits}
        onConfirm={() => {
          void scheduleCancellation();
        }}
        onClose={() => setCancelModalOpen(false)}
      />

      <ChangePlanModal
        open={!!changeModalTarget}
        loading={changing}
        direction={changeModalTarget?.direction ?? 'upgrade'}
        newPlanName={changeModalTarget?.planName ?? ''}
        periodEndLabel={periodEndLabel}
        paidFirst={changeModalTarget?.paidFirst ?? false}
        onConfirm={() => {
          if (changeModalTarget) {
            void changeSubscriptionPlan({ variables: { newPriceId: changeModalTarget.priceId } });
          }
        }}
        onClose={() => setChangeModalTarget(null)}
      />
    </div>
  );
}
